import React, { useMemo } from 'react';
import MetricView from '../common/MetricView';
import { displayTime, getBusiness, createRoleKey, isFeatureFlagEnabled } from '../../../utils';
import { chainWalk } from '../../../../../utils/helpers';
import { BUSINESS_VIEW_NAME, SSD_FEATURE_FLAGS_LIST } from '../../../../../utils/constants';
const PROCESS_PATH_OUTBOUND = 'outbound';

const aggregateDemandEntries = (demand) => {
  const aggregateVolumes = [];
  demand.forEach((volume) => {
    const aggregateVolume = aggregateVolumes.find(
      (aggregateVolume) =>
        aggregateVolume.volume_source === volume.volume_source && aggregateVolume.process_role === volume.process_role
    );
    if (aggregateVolume) {
      aggregateVolume.volume += volume.volume || 0;
    } else {
      aggregateVolumes.push({ ...volume });
    }
  });
  return aggregateVolumes;
};

const ShiftOutboundVolume = ({ shift, demand, businessType = 'core', plan }) => {
  const cptList = shift ? [...new Set(chainWalk(() => demand.map(({ cpt_local }) => cpt_local), []))] : [];
  cptList.sort();
  const isGroceryEnabled =
    plan && isFeatureFlagEnabled(plan.input.adminSettings, SSD_FEATURE_FLAGS_LIST.groceryEnabled);

  // Create component-level business mapping to avoid module-level race conditions
  const roleToBusinessDict = useMemo(() => {
    if (!isGroceryEnabled || !plan) return {};
    const mapping = {};
    const roleBusinessMapping = chainWalk(
      () => JSON.parse(plan.output.serializedOutput).transformedInputs.role_to_business_mapping,
      []
    );
    roleBusinessMapping.forEach((entry) => {
      const processPath = entry.process_path || '';
      const process = entry.process || 'nan';
      const processRole = entry.process_role || '';
      const business = entry.business_type || BUSINESS_VIEW_NAME.CORE;
      const roleKey = `${processPath}__${process}__${processRole}`;
      mapping[roleKey] = business === '' || !business ? BUSINESS_VIEW_NAME.CORE : business.toLowerCase();
    });
    return mapping;
  }, [isGroceryEnabled, plan]);

  const volumeByCpt = useMemo(() => {
    const result = [];

    for (let i = 0; i < cptList.length; i++) {
      const cpt = cptList[i];
      if (cpt > shift.start) {
        let volumeList;

        if (isGroceryEnabled) {
          // Filter volumes by business type using component-level business mapping
          volumeList = demand.filter(({ cpt_local, process_path, process_role, process }) => {
            if (cpt_local !== cpt || process_path !== PROCESS_PATH_OUTBOUND) return false;
            const roleKey = createRoleKey(process_path, process, process_role);
            const roleBusiness = roleToBusinessDict[roleKey] || BUSINESS_VIEW_NAME.CORE;
            return roleBusiness === businessType;
          });
        } else {
          volumeList = demand.filter(
            ({ cpt_local, process_path }) => cpt_local === cpt && process_path === PROCESS_PATH_OUTBOUND
          );
        }
        const total = volumeList.reduce((t, { volume }) => t + volume, 0);
        const aggregateVolumes = aggregateDemandEntries(volumeList);
        result.push({ cpt, total: total.toFixed(0), volumes: aggregateVolumes });
        if (cpt >= shift.end) {
          break;
        }
      }
    }
    return result;
  }, [cptList, shift, demand, businessType, isGroceryEnabled, roleToBusinessDict]);
  return (
    <MetricView
      title={'OB Planned Volume (units)'}
      values={volumeByCpt.map(({ cpt, total, volumes }) => ({
        title: `CPT ${displayTime(cpt)}`,
        subtext: total,
        tooltipValues: volumes.map(
          (volume) => `${volume.volume_source} ${volume.process_role}: ${chainWalk(() => volume.volume.toFixed(0), '')}`
        )
      }))}
    />
  );
};
export default ShiftOutboundVolume;
